/**
 * Pure geometry for the free-form floor editor (Mesas/Espacios). Marker
 * coordinates are permille (0–1000) of the floor canvas, stored in the same
 * grid_x/y/w/h integer columns the old 8×6 grid used. Rows written by the
 * grid era are detected by size — a real permille marker is never narrower
 * than MIN_MARKER_SIZE — and rescaled for display; their permille position is
 * persisted the first time the owner moves them.
 */

export const PERMILLE = 1000;
export const DEFAULT_MARKER_SIZE = 90;
export const ROOM_MARKER_SIZE = 150;
export const MIN_MARKER_SIZE = 40;
export const READABLE_CIRCLE_DIAMETER = 100;
export const READABLE_SQUARE_SIZE = 84;
export const READABLE_RECTANGLE_MIN_WIDTH = 96;
export const READABLE_RECTANGLE_MIN_HEIGHT = 68;
export const PROPOSAL_GAP = 12;
export const CONTENT_PADDING = 60;
export const DERIVED_ZONE_PADDING = 35;

/** The old grid the legacy editor drew: 8 columns × 6 rows. */
const LEGACY_COLS = 8;
const LEGACY_ROWS = 6;

export type MarkerRect = { gridX: number; gridY: number; gridW: number; gridH: number };
export type MarkerShape = 'circle' | 'square' | 'rectangle';
export type ProposedRect = { kind: string; x: number; y: number; w: number; h: number; rotation?: number };
export type PositionedRect = { x: number; y: number; w: number; h: number };
export type ZoneRect = PositionedRect & { name: string };
export type ZoneCategory = 'outdoor' | 'indoor' | 'bar';

const ZONE_TONES = 6;

/** Stable zone color: every table in Salón keeps the same tone across renders. */
export function zoneTone(zone: string | undefined): number {
  const label = zone?.trim().toLocaleLowerCase('es-PA') || 'sin zona';
  let hash = 0;
  for (const character of label) hash = (hash * 31 + character.charCodeAt(0)) >>> 0;
  return hash % ZONE_TONES;
}

const OUTDOOR_ZONE_PATTERN = /\b(?:patio|terraza|exterior|jard[ií]n|balc[oó]n|azotea|rooftop|vereda)\b/iu;
const BAR_ZONE_PATTERN = /\b(?:bar|barra)\b/iu;

/** Semantic palette shared by detected, saved, and manually named zones. */
export function zoneCategory(zone: string | undefined): ZoneCategory {
  const label = zone?.trim() ?? '';
  if (BAR_ZONE_PATTERN.test(label)) return 'bar';
  if (OUTDOOR_ZONE_PATTERN.test(label)) return 'outdoor';
  return 'indoor';
}

export function defaultShapeForKind(kind: string): MarkerShape {
  if (kind === 'bike' || kind === 'mat') return 'circle';
  if (kind === 'room' || kind === 'bar') return 'rectangle';
  return 'square';
}

/** Size-based heuristic: a permille marker is always ≥ MIN_MARKER_SIZE wide. */
export function isLegacyRow(rect: MarkerRect): boolean {
  return rect.gridW <= LEGACY_COLS && rect.gridH <= LEGACY_ROWS;
}

/** Where a row draws: legacy cells are projected into permille space. */
export function displayRect<T extends MarkerRect & { kind?: string; shape?: MarkerShape }>(rect: T): T {
  if (!isLegacyRow(rect)) return rect;
  return {
    ...rect,
    gridX: Math.round(rect.gridX * (PERMILLE / LEGACY_COLS)),
    gridY: Math.round(rect.gridY * (PERMILLE / LEGACY_ROWS)),
    gridW: Math.max(DEFAULT_MARKER_SIZE, Math.round(rect.gridW * (PERMILLE / LEGACY_COLS) * 0.75)),
    gridH: Math.max(DEFAULT_MARKER_SIZE, Math.round(rect.gridH * (PERMILLE / LEGACY_ROWS) * 0.75)),
  };
}

export function clampPermille(value: number, size: number): number {
  return Math.min(Math.max(0, Math.round(value)), PERMILLE - size);
}

/** Pointer position → marker top-left in permille, marker centered under the pointer. */
export function pointerToPermille(
  canvas: { left: number; top: number; width: number; height: number },
  clientX: number,
  clientY: number,
  markerW: number,
  markerH: number,
): { x: number; y: number } {
  const x = ((clientX - canvas.left) / canvas.width) * PERMILLE - markerW / 2;
  const y = ((clientY - canvas.top) / canvas.height) * PERMILLE - markerH / 2;
  return { x: clampPermille(x, markerW), y: clampPermille(y, markerH) };
}

function visibleBounds(rect: ProposedRect): PositionedRect {
  const rotation = ((rect.rotation ?? 0) * Math.PI) / 180;
  const cos = Math.abs(Math.cos(rotation));
  const sin = Math.abs(Math.sin(rotation));
  const w = rect.w * cos + rect.h * sin;
  const h = rect.w * sin + rect.h * cos;
  return {
    x: rect.x + (rect.w - w) / 2,
    y: rect.y + (rect.h - h) / 2,
    w,
    h,
  };
}

const proposedOverlap = (a: ProposedRect, b: ProposedRect, gap: number) => {
  const visibleA = visibleBounds(a);
  const visibleB = visibleBounds(b);
  return (
    visibleA.x < visibleB.x + visibleB.w + gap &&
    visibleA.x + visibleA.w + gap > visibleB.x &&
    visibleA.y < visibleB.y + visibleB.h + gap &&
    visibleA.y + visibleA.h + gap > visibleB.y
  );
};

/**
 * Removes only local proposal collisions. Rooms are fixed source regions;
 * every other resource stays at its source position when possible, otherwise
 * it moves to the nearest free edge around an already placed resource. This
 * deliberately avoids global scaling/cropping, which would erase the visual
 * relationship with the uploaded floor plan.
 */
export function separateFloorPlanResources<T extends ProposedRect>(
  resources: readonly T[],
  gap = PROPOSAL_GAP,
): T[] {
  const architecture = resources.filter(
    (resource) => resource.kind === 'room' || resource.kind === 'bar',
  );
  const adjusted = new Map<T, T>();
  const suppressed = new Set<T>();
  // Distinct rooms and bars are authoritative source geometry. Duplicate
  // detection belongs to the vision sanitizer, not this collision pass.
  const placed: ProposedRect[] = [...architecture];

  for (const resource of resources) {
    if (resource.kind === 'room' || resource.kind === 'bar') continue;
    const source = fitProposedRect(resource);
    if (!placed.some((other) => proposedOverlap(source, other, gap))) {
      adjusted.set(resource, source);
      placed.push(source);
      continue;
    }

    const maxX = Math.max(0, PERMILLE - source.w);
    const maxY = Math.max(0, PERMILLE - source.h);
    const sourceBounds = visibleBounds(source);
    const offsetX = sourceBounds.x - source.x;
    const offsetY = sourceBounds.y - source.y;
    const xs = new Set([source.x, 0, maxX]);
    const ys = new Set([source.y, 0, maxY]);
    for (const obstacle of placed) {
      const obstacleBounds = visibleBounds(obstacle);
      xs.add(clampPermille(obstacleBounds.x - gap - sourceBounds.w - offsetX, source.w));
      xs.add(clampPermille(obstacleBounds.x + obstacleBounds.w + gap - offsetX, source.w));
      ys.add(clampPermille(obstacleBounds.y - gap - sourceBounds.h - offsetY, source.h));
      ys.add(clampPermille(obstacleBounds.y + obstacleBounds.h + gap - offsetY, source.h));
    }

    const candidates = [...xs].flatMap((x) => [...ys].map((y) => ({ ...source, x, y })));
    candidates.sort((a, b) => {
      const distanceA = (a.x - source.x) ** 2 + (a.y - source.y) ** 2;
      const distanceB = (b.x - source.x) ** 2 + (b.y - source.y) ** 2;
      return distanceA - distanceB || a.y - b.y || a.x - b.x;
    });
    const nearest = candidates.find(
      (candidate) => !placed.some((other) => proposedOverlap(candidate, other, gap)),
    );
    if (!nearest) {
      // Rooms and bars are authoritative architecture. If they leave no valid
      // space, suppress a conflicting table instead of drawing through them.
      suppressed.add(resource);
      continue;
    }
    adjusted.set(resource, nearest);
    placed.push(nearest);
  }

  return resources
    .filter((resource) => !suppressed.has(resource))
    .map((resource) => adjusted.get(resource) ?? resource);
}

function fitProposedRect<T extends ProposedRect>(resource: T): T {
  return {
    ...resource,
    x: clampPermille(resource.x, resource.w),
    y: clampPermille(resource.y, resource.h),
  };
}

/**
 * Gives every detected table shape enough visual room for its name and
 * capacity. Growth is centered on the detected footprint, so the source
 * topology stays faithful; rooms and non-table resources retain their
 * architectural size.
 */
export function makeFloorPlanTablesReadable<
  T extends ProposedRect & { shape?: MarkerShape },
>(resources: readonly T[]): T[] {
  return resources.map((resource) => {
    if (resource.kind !== 'table') return resource;
    return { ...resource, ...readableTableRect(resource) };
  });
}

function readableTableRect<T extends PositionedRect & { shape?: MarkerShape }>(rect: T): T {
  const centerX = rect.x + rect.w / 2;
  const centerY = rect.y + rect.h / 2;
  const isRound = rect.shape === 'circle';
  const isSquare = rect.shape === 'square';
  const diameter = Math.max(rect.w, rect.h, READABLE_CIRCLE_DIAMETER);
  const squareSize = Math.max(rect.w, rect.h, READABLE_SQUARE_SIZE);
  const w = isRound ? diameter : isSquare ? squareSize : Math.max(rect.w, READABLE_RECTANGLE_MIN_WIDTH);
  const h = isRound ? diameter : isSquare ? squareSize : Math.max(rect.h, READABLE_RECTANGLE_MIN_HEIGHT);
  return {
    ...rect,
    x: clampPermille(centerX - w / 2, w),
    y: clampPermille(centerY - h / 2, h),
    w,
    h,
  };
}

/**
 * Final restaurant proposal normalization. The model identifies resources and
 * their relationships; this deterministic pass turns that detection into an
 * operational floor: all table shapes become readable, centers use the whole
 * grid, collisions are removed, and open zones are rebuilt around the final
 * table positions. This runs for every future restaurant analysis before save.
 */
export function normalizeRestaurantFloorPlan<
  T extends ProposedRect & { shape?: MarkerShape; zone?: string },
  Z extends ZoneRect,
>(resources: readonly T[], zones: readonly Z[]): { resources: T[]; zones: ZoneRect[] } {
  if (resources.length === 0) return { resources: [], zones: [] };

  const readable = makeFloorPlanTablesReadable(resources);
  const distributed = distributeResourceCenters(readable);
  const separated = separateFloorPlanResources(distributed);
  const openZoneNames = new Set(zones.map((zone) => zone.name.toLocaleLowerCase('es-PA')));
  const rebuiltZones = deriveProposalZoneRegions(
    separated.filter(
      (resource) => resource.kind === 'table' && resource.zone && openZoneNames.has(resource.zone.toLocaleLowerCase('es-PA')),
    ),
  );
  return { resources: separated, zones: rebuiltZones };
}

function distributeResourceCenters<T extends ProposedRect>(resources: readonly T[]): T[] {
  const movable = resources.filter(
    (resource) => resource.kind !== 'room' && resource.kind !== 'bar',
  );
  if (movable.length === 0) return [...resources];
  const centersX = movable.map((resource) => resource.x + resource.w / 2);
  const centersY = movable.map((resource) => resource.y + resource.h / 2);
  const minX = Math.min(...centersX);
  const maxX = Math.max(...centersX);
  const minY = Math.min(...centersY);
  const maxY = Math.max(...centersY);
  const spanX = maxX - minX;
  const spanY = maxY - minY;
  return resources.map((resource) => {
    if (resource.kind === 'room' || resource.kind === 'bar') return resource;
    const centerX = resource.x + resource.w / 2;
    const centerY = resource.y + resource.h / 2;
    const availableX = PERMILLE - CONTENT_PADDING * 2 - resource.w;
    const availableY = PERMILLE - CONTENT_PADDING * 2 - resource.h;
    const x = spanX === 0
      ? (PERMILLE - resource.w) / 2
      : CONTENT_PADDING + ((centerX - minX) / spanX) * availableX;
    const y = spanY === 0
      ? (PERMILLE - resource.h) / 2
      : CONTENT_PADDING + ((centerY - minY) / spanY) * availableY;
    return { ...resource, x: clampPermille(x, resource.w), y: clampPermille(y, resource.h) };
  });
}

function deriveProposalZoneRegions<T extends ProposedRect & { zone?: string }>(resources: readonly T[]): ZoneRect[] {
  const groups = new Map<string, { name: string; rects: T[] }>();
  for (const resource of resources) {
    const name = resource.zone?.trim();
    if (!name) continue;
    const key = name.toLocaleLowerCase('es-PA');
    const group = groups.get(key) ?? { name, rects: [] };
    group.rects.push(resource);
    groups.set(key, group);
  }
  return [...groups.values()].map(({ name, rects }) => {
    const minX = Math.min(...rects.map((rect) => rect.x));
    const minY = Math.min(...rects.map((rect) => rect.y));
    const maxX = Math.max(...rects.map((rect) => rect.x + rect.w));
    const maxY = Math.max(...rects.map((rect) => rect.y + rect.h));
    const x = Math.max(0, minX - DERIVED_ZONE_PADDING);
    const y = Math.max(0, minY - DERIVED_ZONE_PADDING);
    return {
      name,
      x,
      y,
      w: Math.min(PERMILLE, maxX + DERIVED_ZONE_PADDING) - x,
      h: Math.min(PERMILLE, maxY + DERIVED_ZONE_PADDING) - y,
    };
  });
}

/** Assigns an unlabeled resource to the smallest named region containing its center. */
export function assignResourcesToZones<T extends ProposedRect & { zone?: string }>(
  resources: readonly T[],
  zones: readonly ZoneRect[],
): T[] {
  return resources.map((resource) => {
    if (resource.zone?.trim() || resource.kind !== 'table') return resource;
    const centerX = resource.x + resource.w / 2;
    const centerY = resource.y + resource.h / 2;
    const zone = zones
      .filter(
        (candidate) =>
          centerX >= candidate.x &&
          centerX <= candidate.x + candidate.w &&
          centerY >= candidate.y &&
          centerY <= candidate.y + candidate.h,
      )
      .sort((a, b) => a.w * a.h - b.w * b.h)[0];
    return zone ? { ...resource, zone: zone.name } : resource;
  });
}

/**
 * Reframes detected content into the canvas without changing its proportions.
 * One uniform scale applies to resources and zones, so their relative
 * distances, dimensions, and containment survive the crop.
 */
export function reframeFloorPlan<T extends PositionedRect, Z extends PositionedRect>(
  resources: readonly T[],
  zones: readonly Z[],
  padding = CONTENT_PADDING,
): { resources: T[]; zones: Z[] } {
  const content = [...resources, ...zones];
  if (content.length === 0) return { resources: [], zones: [] };

  const minX = Math.min(...content.map((rect) => rect.x));
  const minY = Math.min(...content.map((rect) => rect.y));
  const maxX = Math.max(...content.map((rect) => rect.x + rect.w));
  const maxY = Math.max(...content.map((rect) => rect.y + rect.h));
  const width = Math.max(1, maxX - minX);
  const height = Math.max(1, maxY - minY);
  const available = PERMILLE - padding * 2;
  const scale = Math.min(available / width, available / height);
  const offsetX = (PERMILLE - width * scale) / 2;
  const offsetY = (PERMILLE - height * scale) / 2;
  const transform = <R extends PositionedRect>(rect: R): R => ({
    ...rect,
    x: Math.round(offsetX + (rect.x - minX) * scale),
    y: Math.round(offsetY + (rect.y - minY) * scale),
    w: Math.max(1, Math.round(rect.w * scale)),
    h: Math.max(1, Math.round(rect.h * scale)),
  });
  return { resources: resources.map(transform), zones: zones.map(transform) };
}

/** Reconstructs open named regions from persisted table zone labels. */
export function deriveZoneRegions<T extends MarkerRect & { kind: string; zone?: string }>(
  resources: readonly T[],
  padding = DERIVED_ZONE_PADDING,
): ZoneRect[] {
  const groups = new Map<string, { name: string; rects: MarkerRect[] }>();
  for (const resource of resources) {
    const name = resource.zone?.trim();
    if (!name || resource.kind !== 'table') continue;
    const key = name.toLocaleLowerCase('es-PA');
    const group = groups.get(key) ?? { name, rects: [] };
    group.rects.push(displayRect(resource));
    groups.set(key, group);
  }
  return [...groups.values()].map(({ name, rects }) => {
    const minX = Math.min(...rects.map((rect) => rect.gridX));
    const minY = Math.min(...rects.map((rect) => rect.gridY));
    const maxX = Math.max(...rects.map((rect) => rect.gridX + rect.gridW));
    const maxY = Math.max(...rects.map((rect) => rect.gridY + rect.gridH));
    const x = Math.max(0, minX - padding);
    const y = Math.max(0, minY - padding);
    return {
      name,
      x,
      y,
      w: Math.min(PERMILLE, maxX + padding) - x,
      h: Math.min(PERMILLE, maxY + padding) - y,
    };
  });
}

const overlaps = (a: MarkerRect, b: MarkerRect) =>
  a.gridX < b.gridX + b.gridW &&
  a.gridX + a.gridW > b.gridX &&
  a.gridY < b.gridY + b.gridH &&
  a.gridY + a.gridH > b.gridY;

/** First free lattice spot for a new marker; the center when the floor is full. */
export function freeSpot(existing: MarkerRect[]): { x: number; y: number } {
  const drawn = existing.map(displayRect);
  const step = DEFAULT_MARKER_SIZE + 20;
  for (let y = 20; y + DEFAULT_MARKER_SIZE <= PERMILLE; y += step) {
    for (let x = 20; x + DEFAULT_MARKER_SIZE <= PERMILLE; x += step) {
      const candidate = { gridX: x, gridY: y, gridW: DEFAULT_MARKER_SIZE, gridH: DEFAULT_MARKER_SIZE };
      if (!drawn.some((rect) => overlaps(candidate, rect))) return { x, y };
    }
  }
  return { x: (PERMILLE - DEFAULT_MARKER_SIZE) / 2, y: (PERMILLE - DEFAULT_MARKER_SIZE) / 2 };
}

const KIND_LABELS: Record<string, string> = {
  table: 'Mesa',
  room: 'Cuarto',
  bar: 'Bar',
  bike: 'Bici',
  mat: 'Mat',
};

export function kindLabel(kind: string): string {
  return KIND_LABELS[kind] ?? 'Lugar';
}
